import { useState, useEffect, useCallback, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const ClientMessagesTab = () => {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollerRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/messages/threads`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setThreads(d.threads || []);
      }
    } catch {}
    setLoading(false);
  }, []);

  const loadThread = useCallback(async (tid) => {
    if (!tid) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/messages/threads/${tid}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setActiveThread(d.thread);
        setMessages(d.messages || []);
        // refresh inbox to clear unread badge
        loadThreads();
      }
    } catch {}
  }, [loadThreads]);

  useEffect(() => { loadThreads(); const t = setInterval(loadThreads, 90000); return () => clearInterval(t); }, [loadThreads]);
  useEffect(() => { if (activeId) loadThread(activeId); }, [activeId, loadThread]);
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const sendReply = async () => {
    setError("");
    if (!reply.trim() || !activeId) return;
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/messages/threads/${activeId}/reply`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: reply.trim() })
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) {
        setReply("");
        await loadThread(activeId);
      } else {
        setError(d.detail || "Send failed.");
      }
    } catch {
      setError("Network error.");
    }
    setSending(false);
  };

  return (
    <div data-testid="client-messages-tab" style={{ padding: "0 0.5rem" }}>
      <div style={{ marginBottom: "1.25rem" }}>
        <h1 style={{ color: "#fff", fontSize: "1.65rem", fontWeight: 800, margin: "0 0 0.4rem", letterSpacing: "-0.01em" }}>Client Messages</h1>
        <p style={{ color: "#ADBED1", fontSize: "0.92rem", margin: 0, lineHeight: 1.5 }}>
          Two-way thread with every client. They text from their portal, you reply here. Email notification fires to you on every new message.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(260px, 320px) 1fr", gap: "1rem", minHeight: 480 }}>
        {/* Thread list */}
        <div data-testid="threads-list" style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "0.5rem", overflowY: "auto", maxHeight: 600 }}>
          {loading ? (
            <p style={{ color: "#7E92A8", fontSize: "0.82rem", padding: "0.85rem", textAlign: "center" }}>Loading…</p>
          ) : threads.length === 0 ? (
            <p style={{ color: "#7E92A8", fontSize: "0.82rem", padding: "0.85rem", textAlign: "center", fontStyle: "italic" }}>
              No client messages yet. When a client texts from their portal, they show here.
            </p>
          ) : threads.map(t => {
            const unread = (t.unread_admin || 0) > 0;
            const active = t.thread_id === activeId;
            return (
              <button
                key={t.thread_id}
                onClick={() => setActiveId(t.thread_id)}
                data-testid={`thread-${t.thread_id}`}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: active ? "rgba(0,255,255,0.08)" : "transparent",
                  border: "none",
                  borderLeft: `3px solid ${active ? "#00FFFF" : "transparent"}`,
                  padding: "0.75rem 0.85rem",
                  cursor: "pointer",
                  borderRadius: 4,
                  marginBottom: 2,
                  display: "block",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ color: "#fff", fontWeight: 700, fontSize: "0.85rem" }}>{t.user_name || t.user_email}</span>
                  {unread && (
                    <span data-testid={`thread-unread-${t.thread_id}`} style={{ background: "#00DC78", color: "#030B1A", padding: "0.1rem 0.4rem", borderRadius: 8, fontSize: "0.62rem", fontWeight: 800 }}>
                      {t.unread_admin}
                    </span>
                  )}
                </div>
                <div style={{ color: "#ADBED1", fontSize: "0.74rem", marginTop: "0.2rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.last_message_preview || "—"}
                </div>
                <div style={{ color: "#7E92A8", fontSize: "0.66rem", marginTop: "0.2rem" }}>
                  {fmtTime(t.last_message_at)}
                </div>
              </button>
            );
          })}
        </div>

        {/* Thread view */}
        <div data-testid="thread-view" style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "1rem 1.15rem", display: "flex", flexDirection: "column" }}>
          {!activeId ? (
            <p style={{ color: "#7E92A8", fontSize: "0.85rem", textAlign: "center", margin: "auto" }}>Pick a conversation to view & reply.</p>
          ) : (
            <>
              {activeThread && (
                <div style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: "0.65rem", marginBottom: "0.85rem" }}>
                  <div style={{ color: "#fff", fontSize: "0.95rem", fontWeight: 800 }}>{activeThread.user_name || "Client"}</div>
                  <div style={{ color: "#5AC8D8", fontSize: "0.78rem" }}>{activeThread.user_email}</div>
                  {activeThread.project_id && (
                    <div style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: "0.2rem", fontStyle: "italic" }}>Project: {activeThread.project_id}</div>
                  )}
                </div>
              )}
              <div ref={scrollerRef} style={{ flex: 1, overflowY: "auto", maxHeight: 380, marginBottom: "0.75rem" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
                  {messages.map(m => {
                    const fromAdmin = m.sender_role === "admin";
                    return (
                      <div key={m.message_id} style={{ alignSelf: fromAdmin ? "flex-end" : "flex-start", maxWidth: "80%" }}>
                        <div style={{ background: fromAdmin ? "#00DC78" : "rgba(255,255,255,0.05)", color: fromAdmin ? "#030B1A" : "#fff", padding: "0.55rem 0.85rem", borderRadius: 12, border: fromAdmin ? "none" : "1px solid rgba(255,255,255,0.10)", fontSize: "0.86rem", lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {m.body}
                        </div>
                        <div style={{ fontSize: "0.66rem", color: "#7E92A8", marginTop: "0.18rem", textAlign: fromAdmin ? "right" : "left" }}>
                          {fromAdmin ? "You" : (m.sender_name || "Client")} · {fmtTime(m.created_at)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: "flex", gap: "0.55rem", alignItems: "flex-end", borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "0.75rem" }}>
                <textarea
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) sendReply(); }}
                  placeholder="Type your reply… (Ctrl/Cmd+Enter to send)"
                  rows={2}
                  data-testid="admin-reply-input"
                  style={{ flex: 1, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", padding: "0.6rem 0.8rem", borderRadius: 6, fontSize: "0.85rem", fontFamily: "inherit", resize: "vertical", minHeight: 56 }}
                />
                <button
                  onClick={sendReply}
                  disabled={!reply.trim() || sending}
                  data-testid="admin-reply-send"
                  style={{ background: reply.trim() && !sending ? "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)" : "rgba(255,255,255,0.06)", color: reply.trim() && !sending ? "#030B1A" : "#7E92A8", border: "none", padding: "0.7rem 1.25rem", borderRadius: 6, fontWeight: 800, fontSize: "0.84rem", cursor: reply.trim() && !sending ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}
                >
                  {sending ? "Sending…" : "Reply"}
                </button>
              </div>
              {error && <p data-testid="admin-reply-error" style={{ color: "#FF8B6B", fontSize: "0.78rem", margin: "0.45rem 0 0" }}>✗ {error}</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientMessagesTab;
